import type { HttpMethod } from '@/api/client/http';
import type { TPSDocument, TPSOrderInfo } from '@/company-portal/add-file/add-file.types';
import type { DsMultiSelectOptionBase } from '@/components/design-system/multiselect/multi-select.types';
import type { InquiryConfig } from '@/components/Views/Clients/VerifyIdentity/controllerTypes';
import type { DocumentTemplateOption } from '@/components/Views/Company/File/Documents/document-upload-utils';
import type { Company, Insurance } from '@/types/closinglock.types';

export type AuthenticationMethod = 'Multi-factor (email, phone)' | null;
export type PhoneType = 'mobile';

// note: this ordering is from most to least common
export enum ClientTypeEnum {
    BUYER = 'buyer',
    SELLER = 'seller',
    AGENT = 'agent',
    ATTORNEY = 'attorney',
    LENDER = 'lender',
    OTHER = 'other',
}

export type ClientType = ClientTypeEnum[keyof ClientTypeEnum];

export const CLIENT_TYPE_ORDER: ClientType[] = [
    ClientTypeEnum.BUYER,
    ClientTypeEnum.SELLER,
    ClientTypeEnum.AGENT,
    ClientTypeEnum.ATTORNEY,
    ClientTypeEnum.LENDER,
    ClientTypeEnum.OTHER,
];

export const CLIENT_TYPE_ORDER_MAP = new Map<ClientType, number>(CLIENT_TYPE_ORDER.map((type, index) => [type, index]));

export type UserType = 'Admin' | 'User';
export type UploaderType = 'client' | 'user';

export enum DocumentStatus {
    NOT_RETRIEVED = 'Not Retrieved',
    RETRIEVED = 'Retrieved',
    PENDING_IDV = 'Pending Identity Verification',
    BLOCKED_BY_IDV = 'Blocked by IDV',
    FULLY_EXECUTED = 'Fully Executed',
    NOT_SIGNED = 'Not Signed',
    SIGNED = 'Signed',
    SENT = 'Sent',
    REQUESTED = 'Requested',
    RECEIVED = 'Received',
}

export type PaymentStatus = 'approved' | 'completed' | 'failed' | 'held' | 'pending' | 'rejected';
export type PaymentType = 'ach' | 'wire';
export type PaymentDirection = 'debit' | 'credit';
export type PaymentClassification = 'buyer_ctc' | string;
export type RawIdvStatus = 'requested' | 'success' | 'failed' | 'confirmed_failed' | 'amended' | 'overridden';
export type EsignResult = 'signing_success' | null;

export type CompletedIdvStatuses = 'failed' | 'confirmed_failed' | 'success' | 'amended' | 'overridden';
export type FailedIdvStatuses = 'failed' | 'confirmed_failed';
export type PassedIdvStatuses = 'success' | 'amended' | 'overridden';

export interface FileDetailApiResponse {
    data: FileDetails;
}

export interface Title {
    id: number;
    name: string;
}

export interface Branch {
    id: number;
    active: number;
    title_id: number;
    integration_setting_id: number | null;
    name: string;
    vendor_matching_string: string;
    allows_client_street_view: number;
    allows_request_bank_info: number;
    enable_bav_automatic: number;
    enable_bav_manual: number;
    support_email: string | null;
    contact_email: string;
    post_close_support_phone: string;
    post_close_support_email: string;
    post_close_support_website: string;
    automated_notify_days: number;
    created_at: string;
    updated_at: string;
    deleted_at: string | null;
    address_line_1: string;
    address_line_2: string;
    address_city: string;
    address_state: string;
    address_zip: string;
    phone: string;
    modern_treasury_counterparty_id: string;
    title: Title;
}

export interface User {
    id: number;
    name: string;
    email: string;
}

export interface UserWithBranch extends User {
    branch_id: number;
}

export interface UserWithBranchesGroup extends DsMultiSelectOptionBase {
    name: string;
    email: string;
    branches: number[];
}

export interface Client {
    id: number | string;
    role: ClientType;
    first: string | null;
    last: string | null;
    email: string;
    phone: string;
    safe_to_delete: boolean;
    safe_to_delete_reason: string;
    idv_status: RawIdvStatus | null;
    identity_verified_at: string | null;
    requested_bank_info: boolean;
    conversations: []; // placeholder
    action_routes: {
        delete: string;
        edit: string;
        notify: string;
        request_idv: string;
        request_bank_info: string;
    };
}

export interface ClientPivot {
    id: number;
    client_id: number;
    file_id: number;
    type: ClientType;
    wire_doc_id: number | null;
    bank_account_id: number | null;
    requested_bank_info: number;
    requested_bank_info_at: string | null;
    requested_bank_info_completed_at: string | null;
    retrieved_at: string | null;
    reminder_sent: boolean;
    wire_fraud_notice_accepted: boolean;
    created_at: string;
    updated_at: string;
    deleted_at: string | null;
    pre_file_id: number | null;
}

export interface DocumentClientPivot {
    id: number;
    client_id: number;
    document_id: number;
    esign_required: number;
    esign_folder_id: string | null;
    esign_token: string | null;
    esign_result: EsignResult;
    viewed_at: string | null;
    signed_at: string | null;
    created_at: string | null;
    updated_at: string | null;
    deleted_at: string | null;
    signer_url: string | null;
}

export interface Document {
    // Common fields
    id: number;
    type: 'document' | 'document_task' | 'wire_doc';
    client_id: number;
    client_first_name: string;
    client_last_name: string;
    client_type: ClientType;
    created_at: string;
    updated_at: string;
    deleted_at: string | null;
    status: DocumentStatus; // Computed status from backend

    // Action routes for document operations
    action_routes: {
        download: {
            url: string | null;
            http_method: HttpMethod | null;
        };
        delete: {
            url: string | null;
            http_method: HttpMethod | null;
        };
        notify: {
            url: string | null;
            http_method: HttpMethod | null;
        };
    };

    // Document-specific fields (null for document_task)
    document_id?: number | null;
    file_id?: number | null;
    path?: string | null;
    name?: string | null;
    download_url?: string | null;
    uploader_type?: UploaderType | null;
    uploader_id?: string | null;

    // E-signature fields (null for document_task)
    is_signed?: boolean | null;
    need_to_sign?: boolean | null;
    fulfilled_signatures?: number | null;
    expected_signatures?: number | null;
    signing_status?: string | null;
    signed_at?: string | null;
    viewed_at?: string | null;

    // Document task specific fields (null for document)
    requested_at?: string | null;
    completed_at?: string | null;
    last_notified?: string | null;
    requested_file_name?: string | null;

    // Additional status fields
    is_active?: boolean | null;
    is_insured?: boolean | null;
    wire_fraud_notice_accepted?: boolean | null;
    is_blocked_by_idv?: boolean | null;
    retrieved_at?: string | null;
}

// Document Upload Types

export type MergedDocumentErrorMap = Map<DocumentUploadMergedErrorKeys, string>;

type DocumentUploadConfigBase = {
    documentId: string;
    formKey: string; // serialized int or whatever comes from TPS docs
    title: string;
    recipients: number[];

    /** Form level field, user editable */
    needsEsignature: boolean;
    signers: number[];
    esignEditId?: string;
    esignEditUrl?: string;

    esignStateInvalidationError: string;
    errors: IndividualDocumentErrors;
    mergedErrors: MergedDocumentErrorMap;
};

export type DocumentBinaryUploadConfig = DocumentUploadConfigBase & {
    file: Blob & { name: string };
};

export type DocumentTemplateUploadConfig = DocumentUploadConfigBase & {
    template: DocumentTemplateOption;
};

export type DocumentTpsUploadConfig = DocumentUploadConfigBase & {
    vendorInfo: {
        vendor: TPSOrderInfo['vendor'];
        vendor_order_id: TPSOrderInfo['vendor_order_id'];
        vendor_transaction_id: TPSOrderInfo['vendor_transaction_id'];
        order_document_id: TPSDocument['id'];
    };
    file_temp_path: TPSDocument['value'];
};

export type DocumentUploadConfig = DocumentBinaryUploadConfig | DocumentTemplateUploadConfig | DocumentTpsUploadConfig;

export interface DocumentUploadPayload<T extends DocumentUploadConfig = DocumentUploadConfig> {
    documents: T[];
    sendNotification: boolean;
}

export interface DocumentUploadErrorResponse {
    message?: string;
    errors?: DocumentUploadError;
}

export enum DocumentUploadApiVersion {
    V1 = 'v1',
    V2 = 'v2',
}

/** Backend error key structure for an array of documents and their delivery setup */
export enum DocumentUploadValidationErrorKeys {
    // specific to v1 upload, absent in v2, 1 indexed
    DOCUMENT_RECIPIENTS = 'documents_recipients',
    DOCUMENT_SIGNERS = 'documents_signers',
    DOCUMENT_SIGN_GENERAL = 'documents_sign',
    DOCUMENT = 'documents',
    // specific to v2 upload, 0 indexed
    DOCUMENTS_CONFIG = 'documents_config.documents',
}

/** Merged error map keys for processed document errors */
export enum DocumentUploadMergedErrorKeys {
    DOCUMENT_LEVEL_ERROR = 'documentLevelError',
    RECIPIENTS_ERROR = 'recipientsError',
    SIGNERS_ERROR = 'signersError',
    SIGN_CHECKBOX_ERROR = 'signCheckboxError',
    ALL_SIGNERS_ERROR = 'allSignerErrors',
}

// Error key format: 'document_recipients.0', 'document_signers.<tpsId>', 'document_sign.0', etc.
// The trailing segment is whatever value the FormData array key carried (numeric index for
// binary uploads, vendor doc id for TPS documents).
export type DocumentErrorIndex = string | number;
export type DocumentUploadErrorKey = `${DocumentUploadValidationErrorKeys}.${DocumentErrorIndex}`;
export type DocumentUploadError = Record<DocumentUploadErrorKey, string[] | string>;

// normalized simplified structure for individual document errors
export type IndividualDocumentErrors = {
    [DocumentUploadValidationErrorKeys.DOCUMENT]: string[];
    [DocumentUploadValidationErrorKeys.DOCUMENT_RECIPIENTS]: string[];
    [DocumentUploadValidationErrorKeys.DOCUMENT_SIGNERS]: string[];
    [DocumentUploadValidationErrorKeys.DOCUMENT_SIGN_GENERAL]: string[];
};

export enum ESignEditSource {
    WEB_CREATE = 'webCreate',
    WEB_UPDATE = 'webUpdate',
    VENDOR = 'vendor',
}

export type DocumentContentOrReference = DocumentBinaryUploadConfig['file'] | DocumentTpsUploadConfig['file_temp_path'];

export interface ESignEditRequest {
    document?: DocumentContentOrReference;
    /**
     * FormData array key used for `documents[<key>]`. The vendor middleware
     * (`AttachVendorOrderDocumentsToRequest`) treats this value as the vendor doc id when fetching
     * the source PDF, so it must match the id the caller used to identify the document.
     */
    documentFormKey: string;
    templateId?: string;
    vendorInfo?: {
        vendor: TPSOrderInfo['vendor'];
        vendor_order_id: TPSOrderInfo['vendor_order_id'];
        vendor_transaction_id: TPSOrderInfo['vendor_transaction_id'];
    };
    signerIds: number[];
    signerEmails?: Record<number, string>;
    source: ESignEditSource;
    branchId: number;
    existingFolderId?: string | null;
}

export interface ESignEditResponse {
    url: string;
    folderID: string;
}

// Document Request Types
export interface DocumentRequest {
    id: string; // UUID for tracking in UI
    requestedFileName: string;
}

export interface DocumentUploadResponse {
    success: boolean;
    warning?: string;
}

export interface DocumentRequestPayload {
    requested_file_names: string[];
    client_email_list: string[];
}

export interface DocumentRequestSelectedClient {
    id: Client['id'];
    email: Client['email'];
}

export interface DocumentRequestState {
    selectedClients: DocumentRequestSelectedClient[];
    documentNames: DocumentRequest[];
}

export interface DocumentRequestErrorResponse {
    message?: string;
    errors?: Record<string, string[]>;
}

export interface WireDoc {
    id: number;
    active: number;
    path: string;
    name: string;
    is_insured: Insurance;
    wire_fraud_notice_accepted: boolean;
    is_blocked_by_idv: boolean;
    retrieved_at: string | null;
    download_url: string;
}

export type UpdateFilePayload = Partial<Pick<FileDetails, 'address' | 'balance_due' | 'close_date' | 'ref_number'>>;

export interface UpdateFileError {
    errors?: Record<string, string[]>;
    message?: string;
}

export interface AvailableWireDoc {
    id: number;
    active: boolean;
    branch_id: Branch['id'];
    company_id: Title['id'];
    path: string;
    name: string;
    payment_start_date: string | null;
    deleted_at: string | null;
    insured: Insurance;
    disabled_at: string | null;
    download_url: string;
}

export interface DsSingleSelectAvailableWireDocOption extends AvailableWireDoc {
    label: AvailableWireDoc['name'];
    value: AvailableWireDoc['id'];
}

export interface File {
    id: number;
    branch_id: number;
    ref_number: string;
    balance_due: number;
    vendor: string;
    vendor_order_id: string;
    vendor_transaction_id: string;
    close_date: string;
    sales_price: string;
    earnest_money: string;
    amount_down: string;
    owned_by_user_id: number;
    address: string;
    street_view_url: string;
    account_number: string;
    routing_number: string;
    created_at: string;
    updated_at: string;
    deleted_at: string;
    notes: string;
}

export interface FileDetailsDocumentRoutes {
    send_wire_doc: string;
    request_wire_doc: string;
    add_document: string;
    add_document_v2: string;
    request_document: string;
    esign_edit: string;
    esign_edit_v2: string;
}
export interface AddFileFormDocumentRoutes {
    // add file form uses v1 api
    // tps has own route with augmented payload and it's populated
    // here to keep code path unified
    esign_edit: string;
}

export type DocumentActionRoutes = FileDetailsDocumentRoutes | AddFileFormDocumentRoutes;

export interface TPSDocumentVendorContext {
    vendor: TPSOrderInfo['vendor'];
    vendor_order_id: TPSOrderInfo['vendor_order_id'];
    vendor_transaction_id: TPSOrderInfo['vendor_transaction_id'];
}

export type TPSDocumentWithContext = TPSDocument & TPSDocumentVendorContext;

export interface FileDetails {
    ref_number: string;
    address: string | null;
    close_date: string | null;
    branch: string; // branch name
    branch_id: number;
    branch_messaging_phone_number: string | null;
    created_at: string;
    updated_at: string;
    balance_due: string | null;
    owned_by: string; // owned by user name
    street_view_url: string | null;
    allows_balance_due: boolean;
    allows_close_date: boolean;
    allows_notes: boolean;
    safe_to_delete: boolean;
    safe_to_delete_reason: string;
    users_with_branches: UserWithBranch[];
    routes: { delete_file: string; edit_file: string; export_file: string; file_history: string };
    clients: {
        routes: { add_client: string; edit_client: string };
        data: Client[];
    };
    documents: {
        routes: FileDetailsDocumentRoutes;
        available_wire_docs: AvailableWireDoc[];
        data: Document[];
        wire_docs: WireDoc[];
    };
    identity_verifications: {
        routes: { add_idv_request: string };
        data: IdentityVerification[];
    };
    bank_account_verifications: {
        routes: { request_bank_account_verification: string };
        data: BankAccountVerification[];
    };
    payments: {
        data: Payment[];
    };
    notifications: {
        routes: { add_notification_recipient: string };
        data: NotificationRecipient[];
    };
    notes: {
        routes: { add_note: string };
        data: string;
    };
}

export interface SendWireDocPayload {
    wireDoc_id: number;
    wireDoc_recipients: number[];
    notify?: string;
}

export interface SendWireDocResponse {
    success: boolean;
    ref_number: File['ref_number'];
    company: Company;
    branch: Branch;
    payments_enabled: boolean;
    document_name: string;
    clients: Array<{ email: string; type: string }>;
}

export interface SendWireDocErrorResponse {
    errors?: {
        wireDoc_id?: string[];
        wireDoc_recipients?: string[];
    };
    message?: string;
}

export interface BankAccountVerification {
    id: number;
    client_id: number;
    first: string;
    last: string;
    email: string;
    phone: string;
    type: ClientType;
    insurance_status: Insurance;
    requested_bank_info: boolean;
    requested_bank_info_at: string | null;
    requested_bank_info_completed_at: string | null;
    reminder_sent: boolean;
    retrieved_at: string | null;
    bav_report: BavReportResponse;
}

export interface RequestWireInformationResponse {
    success: boolean;
    ref_number: string;
    account: {
        id: number;
        name: string;
        legal_address_city: string;
        legal_address_line_1: string;
        legal_address_line_2: string;
        legal_address_state: string;
        legal_address_zip: string;
        legal_business_name: string;
    };
    company: {
        id: number;
        name: string;
    };
    branch: Branch;
    clients: Array<{
        email: string;
        type: string;
    }>;
}

export interface BavReportResponse {
    headers: Record<string, unknown>;
    original: unknown[];
    exception: unknown;
}

export interface NotificationRecipient {
    action_routes: {
        delete_notification_recipient: string;
    };
    id: number | null;
    name: string;
    email: string;
    notified_because: string;
    created_by: string;
    created_at: string;
}

export interface Payment {
    action_routes: {
        download: string;
        refund: string;
    };
    amount: number;
    completed_at: string | null;
    estimated_transfer_time: string | null;
    client_timezone: string | null;
    fedwire_number: string | null;
    id: string;
    insurance: {
        status: Insurance;
        display_text: string;
    };
    receipt_url: string;
    sender: {
        id: string;
        email: string;
        first: string;
        last: string;
        role: string;
        phone: string;
    };
    sent_at: string | null;
    status: {
        code: string;
        display_text: string;
    };
    is_returnable: boolean;
}

export interface IdentityVerification {
    id: number;
    client_id: number;
    first: string;
    last: string;
    email: string;
    phone: string;
    idv_name: string;
    type: ClientType;
    raw_status: RawIdvStatus;
    status: string;
    completed_idv_statuses: CompletedIdvStatuses[];
    passed_idv_statuses: PassedIdvStatuses[];
    failed_idv_statuses: FailedIdvStatuses[];
    idv_status: RawIdvStatus;
    is_idv_passed: boolean;
    is_self_serve: boolean;
    notified_at: string;
    completed_at: string | null;
    can_override: boolean;
    can_delete_idv_request: boolean;
    created_at: string;
    overridden_reason: string | null;
    action_routes: {
        resend: string;
        view: string;
        download: string;
        complete: string;
        delete: string;
    };
    inquiry: InquiryConfig;
    reference_id: string;
}

export interface PaymentSummary {
    id: number;
    payment_base_amount: string;
    payment_created_at_date: string;
    payment_created_at_time: string;
    company_payment_status: string;
    payment_insurance_status: Insurance;
    payment_estimated_send_at: string;
    payment_completed_at_date: string | null;
    payment_completed_at_time: string | null;
    payment_fedwire_number: string | null;
    role: string;
    client_name: string;
    client_email: string;
}

export interface UpdateNotificationRecipientsResponse {
    address: string;
    amount_down: string | null;
    balance_due: string | null;
    branch: Branch;
    branch_id: number;
    close_date: string | null;
    created_at: string;
    deleted_at: string | null;
    earnest_money: string | null;
    id: number;
    notified_at: string | null;
    notifying_users: Array<{
        id: number;
        name: string;
        email: string;
    }>;
    owned_by_user_id: number;
    ref_number: string;
    sales_price: string | null;
    street_view_url: string | null;
    updated_at: string | null;
    uploaded_at: string | null;
    user_email: string;
    vendor: string | null;
    vendor_order_id: string | null;
    vendor_transaction_id: string | null;
}

export interface DeleteIdvRequestResponse {
    success: boolean;
    message?: string;
    errors?: Record<string, string[]>;
}

export interface RequestIdvResponse {
    success: boolean;
    ref_number: string;
    account: {
        id: number;
        name: string;
        legal_business_name: string;
        legal_address_line_1: string;
        legal_address_line_2: string;
        legal_address_city: string;
        legal_address_state: string;
        legal_address_zip: string;
    };
    company: {
        id: number;
        name: string;
    };
    branch: Branch;
    clients: Client[];
}

export interface ClientDeleteDialogProps {
    client: Client | null;
}

export interface ClientDeleteDialogEmits {
    (event: 'deleted'): void;
}

// client option used for adding doc, request doc, sending wire doc
export type ClientOption = DsMultiSelectOptionBase & {
    id: string;
    value: Client['id'];
    label: string;
    email: Client['email'];
    first: Client['first'];
    last: Client['last'];
    role: Client['role'];
    phone?: Client['phone'];
};

export interface DsSingleSelectRoleOption {
    id: ClientTypeEnum;
    label: string;
    value: ClientTypeEnum;
}

export interface AddFileConfirmationDocument {
    name: NonNullable<Document['name']>;
    needsEsign: NonNullable<Document['need_to_sign']>; //per client
}

export interface AddFileConfirmationClientTasks {
    requestWireInstructions: boolean;
    requestIdentityVerification: boolean;
}

export interface AddFileConfirmationClient extends Pick<Client, 'id' | 'role' | 'email' | 'phone'> {
    first: NonNullable<Client['first']>;
    last: NonNullable<Client['last']>;
    tasks: AddFileConfirmationClientTasks;
    wireInstructionsDocumentName: WireDoc['name'] | null;
    documents: AddFileConfirmationDocument[];
}

export interface AddFileConfirmationFileDetails {
    referenceNumber: FileDetails['ref_number'];
    propertyAddress: NonNullable<FileDetails['address']>;
    company: Company['name'];
    branch: FileDetails['branch'];
    closingDate: NonNullable<FileDetails['close_date']>;
    balanceDue: NonNullable<FileDetails['balance_due']>;
}

export interface AddFileConfirmationSummary {
    fileDetails: AddFileConfirmationFileDetails;
    clients: AddFileConfirmationClient[];
}
